import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Pagination from './Pagination.js';
import SelectGuide from './SelectGuide.js';

import { useNavigate } from 'react-router-dom';
import { file_path } from "./environment";


function getResultsPage(items, page, per_page) {
    let start = (page - 1) * per_page;
    let end = page * per_page;
    return items.slice(start, end);
}

function InputFilter({ name, text, onChange }) {
    return (
        <input id={name} onChange={onChange} value={text} placeholder="Search..." />
    );
}

function OptionFilter({ id, value = '', values, onFilterChange }) {
    const options = values.map((option) => {
        return <option value={option} key={option}>{option}</option>
    });

    return (
        <select key={id} id={id} value={value} onChange={onFilterChange}>
            <option value="" key="none"></option>
            {options}
        </select>
    );
}

function Table({ items, years, page, pageSize, filters, onFilterChange }) {
    const years_headers = Object.keys(years).map((year) => {
        return <th key={year}>{year}</th>;
    });

    const years_filters = Object.keys(years).map((year) => {
        return (
            <th key={year} className="year">
                <OptionFilter key={year} id={year} value={filters?.[year] || ''} values={years[year]} onFilterChange={onFilterChange} />
            </th>
        );
    });

    const rows = getResultsPage(items, page, pageSize).map((item, index) => {
        return <Row key={index} item={item} />;
    });

    return (
        <table>
            <thead>
                <tr key="headings">
                    <th key="Name">Name</th>
                    <th key="Practice">Practice</th>
                    {years_headers}
                </tr>
                <tr key="filters">
                    <th key="name"><InputFilter name="name" text={filters?.name || ''} onChange={onFilterChange} /></th>
                    <th key="practice"><InputFilter name="practice" text={filters?.practice || ''} onChange={onFilterChange} /></th>
                    {years_filters}
                </tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>
    );
}

function Row({ item }) {
    const history = Object.keys(item.history).map((year) => {
        return <td key={year} className="year">{item.history[year]}</td>;
    });

    return (
        <tr key={item.id}>
            <td key={item.name}>{item.name}</td>
            <td key={item.practice}>{item.practice}</td>
            {history}
        </tr>
    );
}

export default function L500Firms() {
    const [guides, setGuides] = useState([]);
    const [guide, setGuide] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [allItems, setAllItems] = useState([]);
    const [years, setYears] = useState([]);
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState({});
    const navigate = useNavigate();
    const { guideId } = useParams();

    useEffect(() => {
        document.title = `Legal 500 ${guide.name} Firms`;
    }, [guide.name]);

    function handlePage(newPage) {
        setPage(newPage);
    }

    function handleChangePageSize(event) {
        const newPageSize = event.target.value;
        setPageSize(newPageSize);

        const lastPage = Math.ceil(items.length / newPageSize);
        if (page > lastPage) {
            setPage(lastPage);
        }
    }

    const guideSelected = useCallback((newGuide) => {
        if (!newGuide) {
            return;
        }

        setGuide(newGuide);
        navigate(`/l500/firms/${newGuide.id}`);
        axios.get(file_path(`/l500/firms/${newGuide.url}.json`)).then((res) => {
            setAllItems(res.data.firms);
            setItems(res.data.firms);
            setYears(res.data.years);
        });
    }, [navigate]);

    function handleFilterChange(event) {
        const filterId = event.target.id;
        const filterValue = event.target.value;
        let newFilters = Object.assign({}, filters);
        if (!filterValue) {
            // removing unspecified filters gives fewer comparisons to make, improving performance
            delete newFilters[filterId];
        } else {
            newFilters[filterId] = filterValue;
        }

        setFilters(newFilters);

        let matchingItems = allItems;

        for (const [key, value] of Object.entries(newFilters)) {
            if (parseInt(key)) {
                matchingItems = matchingItems.filter((item) => {
                    return item.history[key].toUpperCase().indexOf(value.toUpperCase()) !== -1;
                })
            } else {
                matchingItems = matchingItems.filter((item) => {
                    return item[key].toUpperCase().indexOf(value.toUpperCase()) !== -1;
                })
            }
        }

        setItems(matchingItems);
        // New search, return to first page of results
        setPage(1);
    }

    function findGuideById(guideList, guideId = 'united-states') {
        return guideList.find((element) => element.id.toString() === guideId.toString());
    }

    useEffect(() => {
        axios.get(file_path('/l500/guides.json')).then((res) => {
            setGuides(res.data.guides);

            const found = findGuideById(res.data.guides, guideId);
            if (found) {
                guideSelected(found);
            }
        });
    }, [guideId, guideSelected]);

    return (
        <div id="directory">
            <nav>
                <Link to="/guides" role="button">Home</Link>
                <Link to={`/l500/firms/${guide.id}`} role="button">Firms</Link>
                <Link to={`/l500/lawyers/${guide.id}`} role="button">Lawyers</Link>
            </nav>
            <h1>{guide.name} Firms - Legal 500</h1>
            <SelectGuide guides={guides} value={guide} guideSelected={guideSelected} />
            <div className="navigation">
                <Pagination page={page} pageSize={pageSize} items={items} onPage={handlePage} onChangePageSizeChange={handleChangePageSize} />
            </div>
            <div className="table">
                <Table items={items} years={years} page={page} pageSize={pageSize} filters={filters} onFilterChange={handleFilterChange} />
            </div>
            <div className="navigation">
                <Pagination page={page} pageSize={pageSize} items={items} onPage={handlePage} onChangePageSizeChange={handleChangePageSize} />
            </div>
        </div>
    );
}
